import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { getItem, registerOutput } from "../../services/Items/Inventory";
import { getFolios, getFoliosByQuery } from "../../services/Configs/ItemFolios";

const BOTTLES_PER_BOX = 12;

const FieldLabel = ({ children, required = false }) => (
    <Form.Label className="output-label fw-bold mb-1">
        {children} {required && <span className="text-danger">*</span>}
    </Form.Label>
);

export default function OutputModal({ show, onClose, itemId, onSuccess }) {
    const [item, setItem] = useState(null);
    const [folios, setFolios] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [fixedFolioLabel, setFixedFolioLabel] = useState("");
    const [fixedFolioId, setFixedFolioId] = useState(null);

    const [form, setForm] = useState({
        outputType: "sale",
        unit: "Botellas",
        quantity: "1",
        date: new Date().toISOString().slice(0, 10),
        notes: "",
        description: "",
        folioMode: "single", // single | auto | range
        folioSearch: "",
        folioId: "",
        startFolioId: "",
        endFolioId: "",

        customerName: "",
        saleUnitPrice: "",
        baseUnitPrice: "",
        promotionUnitPrice: "",
        discountPercent: "",
        discountAmount: "",
        lineTotal: "",
    });

    const baseUnit = item?.unitOfWeightMeasurement || "";
    const stock = Number(item?.quantity || 0);

    const baseQuantity = useMemo(() => {
        const q = Number(form.quantity || 0);
        if (!q) return 0;
        if (form.unit === "Cajas") return q * BOTTLES_PER_BOX;
        return q;
    }, [form.quantity, form.unit]);

    const isSingle = baseQuantity === 1;
    const isMultiple = baseQuantity > 1;

    const availableFoliosCount = folios.length;
    const shouldLockSingleFolio = isSingle && availableFoliosCount === 1;
    const shouldChooseSingleFolio = isSingle && availableFoliosCount > 1;

    const validate = () => {
        if (!item) return "No se pudo cargar el ítem.";
        if (item.stage !== "finished") return "Sólo se puede registrar salida de producto terminado.";

        const q = Number(form.quantity || 0);
        if (!q || q <= 0) return "Cantidad inválida.";
        if (baseQuantity > stock) return "La salida excede el inventario disponible.";

        if (isSingle && !form.folioId) {
            return "Debes seleccionar el folio de la botella.";
        }

        if (isMultiple && form.folioMode === "range" && (!form.startFolioId || !form.endFolioId)) {
            return "Debes seleccionar un rango de folios.";
        }

        if (form.outputType === "sale") {
            if (Number(form.saleUnitPrice || 0) <= 0) {
                return "Debes capturar el precio unitario de venta.";
            }
        }

        if (form.outputType === "promotion") {
            if (Number(form.promotionUnitPrice || 0) <= 0) {
                return "Debes capturar el precio promocional.";
            }
        }

        return null;
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");

            const err = validate();
            if (err) throw new Error(err);

            await registerOutput(itemId, {
                outputType: form.outputType,
                unit: form.unit,
                quantity: Number(form.quantity),
                date: form.date,
                notes: form.notes,
                description: form.description,

                folioMode: form.folioMode,
                folioId: form.folioId || null,
                startFolioId: form.startFolioId || null,
                endFolioId: form.endFolioId || null,

                customerName: form.customerName || null,
                saleUnitPrice: form.saleUnitPrice ? Number(form.saleUnitPrice) : null,

                baseUnitPrice: form.baseUnitPrice ? Number(form.baseUnitPrice) : null,
                promotionUnitPrice: form.promotionUnitPrice ? Number(form.promotionUnitPrice) : null,
                discountPercent: form.discountPercent ? Number(form.discountPercent) : null,
                discountAmount: form.discountAmount ? Number(form.discountAmount) : null,
                lineTotal: form.lineTotal ? Number(form.lineTotal) : null,
            });

            onSuccess?.();
            onClose?.();
        } catch (e) {
            setError(e?.message || "No se pudo registrar la salida.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!show || !itemId) return;

        setError("");
        setFixedFolioId(null);
        setFixedFolioLabel("");
        setFolios([]);

        (async () => {
            const it = await getItem(itemId);
            setItem(it);

            const today = new Date().toISOString().slice(0, 10);
            setForm({
                outputType: "sale",
                unit: "Botellas",
                quantity: "1",
                date: today,
                notes: "",
                description: "Venta",
                folioMode: "single",
                folioSearch: "",
                folioId: "",
                startFolioId: "",
                endFolioId: "",
                customerName: "",
                saleUnitPrice: String(it?.final_price ?? ""),
                baseUnitPrice: String(it?.final_price ?? ""),
                promotionUnitPrice: "",
                discountPercent: "",
                discountAmount: "",
                lineTotal: "",
            });

            const resp = await getFoliosByQuery("", {
                itemId,
                onlyActives: false,
                onlyUnsold: true,
                onlyNotWasted: true,
                page: 1,
                pageSize: 200,
            });

            const rows = resp?.items || [];
            setFolios(rows);

            if (rows.length === 1) {
                setFixedFolioId(rows[0].id);
                setFixedFolioLabel(rows[0].name);

                setForm((f) => ({
                    ...f,
                    folioId: rows[0].id,
                }));
            }
        })();
    }, [show, itemId]);

    useEffect(() => {
        if (!baseQuantity || baseQuantity <= 0) return;

        if (baseQuantity === 1) {
            setForm((f) => ({
                ...f,
                folioMode: "single",
                startFolioId: "",
                endFolioId: "",
            }));
        } else {
            setForm((f) => ({
                ...f,
                folioMode: f.folioMode === "single" ? "auto" : f.folioMode,
                folioId: "",
            }));
        }
    }, [baseQuantity]);

    useEffect(() => {
        if (!isSingle) {
            setFixedFolioId(null);
            setFixedFolioLabel("");
            return;
        }

        if (folios.length === 1) {
            setFixedFolioId(folios[0].id);
            setFixedFolioLabel(folios[0].name);

            setForm((f) => ({
                ...f,
                folioId: folios[0].id,
            }));
        } else {
            setFixedFolioId(null);
            setFixedFolioLabel("");
            setForm((f) => ({
                ...f,
                folioId: "",
            }));
        }
    }, [isSingle, folios]);

    useEffect(() => {
        const map = {
            sale: "Venta",
            courtesy: "Cortesía",
            waste: "Merma",
            promotion: "Promoción",
            tasting: "Degustación",
        };

        setForm((f) => ({
            ...f,
            description: map[f.outputType] || "",
            baseUnitPrice:
                f.outputType === "promotion"
                    ? String(item?.final_price ?? "")
                    : f.baseUnitPrice,
            saleUnitPrice:
                f.outputType === "sale"
                    ? String(item?.final_price ?? "")
                    : f.saleUnitPrice,
        }));
    }, [form.outputType, item?.final_price]);

    useEffect(() => {
        const qty = Number(baseQuantity || 0);

        if (form.outputType === "sale") {
            const unitPrice = Number(form.saleUnitPrice || 0);
            setForm((f) => ({
                ...f,
                lineTotal: qty > 0 && unitPrice > 0 ? (qty * unitPrice).toFixed(2) : "",
            }));
            return;
        }

        if (form.outputType === "promotion") {
            const basePrice = Number(form.baseUnitPrice || 0);
            const promoPrice = Number(form.promotionUnitPrice || 0);

            let discountAmount = 0;
            let discountPercent = 0;

            if (basePrice > 0 && promoPrice >= 0 && promoPrice <= basePrice) {
                discountAmount = basePrice - promoPrice;
                discountPercent = (discountAmount / basePrice) * 100;
            }

            setForm((f) => ({
                ...f,
                discountAmount: discountAmount ? discountAmount.toFixed(2) : "",
                discountPercent: discountPercent ? discountPercent.toFixed(2) : "",
                lineTotal: qty > 0 && promoPrice > 0 ? (qty * promoPrice).toFixed(2) : "",
            }));
        }
    }, [baseQuantity, form.outputType, form.saleUnitPrice, form.baseUnitPrice, form.promotionUnitPrice]);

    return (
        <Modal show={show} onHide={onClose} backdrop="static" dialogClassName="outputing-modal" contentClassName="outputing-modal-content">
            <Modal.Header closeButton>
                <Modal.Title>Registrar salida</Modal.Title>
            </Modal.Header>
            <style>{`
                .outputing-modal-content .output-label {
                    min-height: 22px;
                    display: flex;
                    align-items: center;
                    font-weight: 700;
                    margin-bottom: .35rem;
                    line-height: 1.2;
                }

                .outputing-modal-content .output-section-title {
                    font-weight: 700;
                    margin-bottom: .5rem;
                }

                .outputing-modal-content .form-control,
                .outputing-modal-content .form-select {
                    min-height: 38px;
                }

                .outputing-modal-content textarea.form-control {
                    min-height: 38px;
                }

                .outputing-modal-content .output-dynamic-row {
                    min-height: 86px;
                }

                .outputing-modal-content .output-help-text {
                    min-height: 38px;
                    display: block;
                }
            `}</style>

            <Modal.Body>
                {item && (
                    <Alert variant="secondary" className="py-2">
                        <div><b>Producto:</b> {item.name}</div>
                        <div><b>Etapa:</b> Terminado</div>
                        <div><b>Existencia:</b> {stock.toLocaleString("es-MX")} {baseUnit}</div>
                        <div><b>Unidad base:</b> {baseUnit}</div>
                    </Alert>
                )}

                <Row className="mb-3 g-3">
                    <Col md={4}>
                        <Form.Group>
                            <FieldLabel>Tipo de salida</FieldLabel>
                            <Form.Select
                                value={form.outputType}
                                onChange={(e) => setForm(f => ({ ...f, outputType: e.target.value }))}
                            >
                                <option value="sale">Venta</option>
                                <option value="courtesy">Cortesía</option>
                                <option value="waste">Merma</option>
                                <option value="promotion">Promoción</option>
                                <option value="tasting">Degustación</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <FieldLabel>Unidad</FieldLabel>
                            <Form.Select
                                value={form.unit}
                                onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))}
                            >
                                <option>Botellas</option>
                                <option>Cajas</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group>
                            <FieldLabel>Cantidad</FieldLabel>
                            <Form.Control
                                type="number"
                                min="1"
                                step="1"
                                value={form.quantity}
                                onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
                            />
                            <Form.Text className="output-help-text text-muted">
                                Equivale a {baseQuantity || 0} botellas base
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <div className="output-extra-fields">
                    {form.outputType === "sale" && (
                        <Row className="mb-3 g-3 output-dynamic-row">
                            <Col md={4}>
                                <Form.Group>
                                    <FieldLabel>Cliente</FieldLabel>
                                    <Form.Control
                                        value={form.customerName}
                                        onChange={(e) => setForm(f => ({ ...f, customerName: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <FieldLabel required>Precio de venta</FieldLabel>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.saleUnitPrice}
                                        onChange={(e) => setForm(f => ({ ...f, saleUnitPrice: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <FieldLabel>Total</FieldLabel>
                                    <Form.Control value={form.lineTotal} readOnly />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    {form.outputType === "promotion" && (
                        <>
                            <Row className="mb-3 g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <FieldLabel>Precio base</FieldLabel>
                                        <Form.Control value={form.baseUnitPrice} readOnly />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <FieldLabel required>Precio promoción</FieldLabel>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={form.promotionUnitPrice}
                                            onChange={(e) => setForm(f => ({ ...f, promotionUnitPrice: e.target.value }))}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-3 g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <FieldLabel>% descuento</FieldLabel>
                                        <Form.Control value={form.discountPercent} readOnly />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <FieldLabel>Total</FieldLabel>
                                        <Form.Control value={form.lineTotal} readOnly />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>

                <Row className="mb-3 g-3">
                    <Col md={6}>
                        <Form.Group>
                            <FieldLabel>Fecha</FieldLabel>
                            <Form.Control
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <FieldLabel>Notas</FieldLabel>
                            <Form.Control
                                as="textarea"
                                rows={1}
                                value={form.notes}
                                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <hr />

                <div className="output-section-title">Folios</div>

                {isSingle && shouldLockSingleFolio && (
                    <Row className="g-3">
                        <Col md={12}>
                            <FieldLabel>Folio asignado</FieldLabel>
                            <Form.Control
                                type="text"
                                value={fixedFolioLabel || "Sin folio disponible"}
                                disabled
                                readOnly
                            />
                            <Form.Text className="text-muted">
                                Esta botella solo tiene un folio disponible y se usará automáticamente.
                            </Form.Text>
                        </Col>
                    </Row>
                )}

                {isSingle && shouldChooseSingleFolio && (
                    <Row className="g-3">
                        <Col md={6}>
                            <FieldLabel>Buscar folio</FieldLabel>
                            <Form.Control
                                value={form.folioSearch}
                                onChange={async (e) => {
                                    const q = e.target.value;
                                    setForm(f => ({ ...f, folioSearch: q, folioId: "" }));

                                    const resp = await getFoliosByQuery(q, {
                                        itemId,
                                        onlyActives: false,
                                        onlyUnsold: true,
                                        onlyNotWasted: true,
                                        page: 1,
                                        pageSize: 100,
                                    });

                                    setFolios(resp?.items || []);
                                }}
                            />
                        </Col>

                        <Col md={6}>
                            <FieldLabel required>Folio</FieldLabel>
                            <Form.Select
                                value={form.folioId}
                                onChange={(e) => setForm(f => ({ ...f, folioId: e.target.value }))}
                            >
                                <option value="">Selecciona...</option>
                                {folios.map(fl => (
                                    <option key={fl.id} value={fl.id}>{fl.name}</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Selecciona cuál de los folios asignados a este producto saldrá.
                            </Form.Text>
                        </Col>
                    </Row>
                )}

                {isMultiple && (
                    <>
                        <div className="d-flex gap-3 mb-2 mt-2">
                            <Form.Check
                                type="radio"
                                label="Auto"
                                checked={form.folioMode === "auto"}
                                onChange={() => setForm(f => ({ ...f, folioMode: "auto", startFolioId: "", endFolioId: "" }))}
                            />
                            <Form.Check
                                type="radio"
                                label="Rango manual"
                                checked={form.folioMode === "range"}
                                onChange={() => setForm(f => ({ ...f, folioMode: "range" }))}
                            />
                        </div>

                        {form.folioMode === "range" && (
                            <Row className="g-3">
                                <Col md={6}>
                                    <FieldLabel required>Folio inicial</FieldLabel>
                                    <Form.Select
                                        value={form.startFolioId}
                                        onChange={(e) => setForm(f => ({ ...f, startFolioId: e.target.value }))}
                                    >
                                        <option value="">Selecciona...</option>
                                        {folios.map(fl => (
                                            <option key={fl.id} value={fl.id}>{fl.name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <FieldLabel required>Folio final</FieldLabel>
                                    <Form.Select
                                        value={form.endFolioId}
                                        onChange={(e) => setForm(f => ({ ...f, endFolioId: e.target.value }))}
                                    >
                                        <option value="">Selecciona...</option>
                                        {folios.map(fl => (
                                            <option key={fl.id} value={fl.id}>{fl.name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar salida"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}